import CheckIcon from '@mui/icons-material/Check';
import { Box, FormControl } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useRegistration } from '@/api/mutations/useAuthMutations';
import { BackButton } from '@/components/BackButton/BackButton';
import { FormHelperText } from '@/components/FormHelperText/FormHelperText';
import { LogoSvg } from '@/components/Icons/LogoSvg';
import { Loading } from '@/components/Loading/Loading';
import { Input } from '@/components/StyledComponents';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { paths } from '@/constants';
import { snackbarColor } from '@/enums';
import { useSnackbar, useToken } from '@/hooks';
import type { RegistrationTypes } from '@/types';
import { validateRegistartion } from '@/utils/validators';

export const RegistrationForm = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showSnackbar = useSnackbar();
    const validate = validateRegistartion(t);
    const url = new URL(window.location.toString());
    const code = url.searchParams.get('code');
    const email = url.searchParams.get('email');
    const token = useToken();

    const { mutateAsync: registration, status } = useRegistration();

    const initialValues: RegistrationTypes = {
        email: email || (location.state as string) || '',
        first_name: '',
        last_name: '',
        password: '',
        password_confirmation: '',
    };

    useEffect(() => {
        if (token) navigate(paths.start.index);
    }, [token, navigate]);

    const formik = useFormik({
        validate,
        initialValues,
        validateOnMount: true,
        isInitialValid: false,
        onSubmit: values => {
            if (code) {
                values.code = code;
            }
            registration(values).catch(error => {
                if (error.data.message) {
                    showSnackbar({
                        open: true,
                        message: error.data.message,
                        severity: snackbarColor.error,
                    });
                }
                formik.setSubmitting(false);
            });
        },
    });

    return (
        <Box p="0 15px" textAlign="center" position="relative" width="1075px">
            {code ? null : <BackButton />}

            <div className="flex gap-[25px] mb-[50px] flex-col items-center">
                <LogoSvg />
                {email && (
                    <div className="flex flex-col">
                        <Text variant="subtitle3" children={t('invite-registering')} />
                        <Text variant="subtitle3" color="text-secondary" children={email} />
                    </div>
                )}
            </div>
            <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
                {!email && (
                    <FormControl fullWidth>
                        <Input
                            id="email"
                            name="email"
                            type="text"
                            placeholder={t('email')}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                            endAdornment={!formik.errors.email ? <CheckIcon color="secondary" sx={{ fontSize: '0.875rem' }} /> : null}
                        />
                        <div className="min-h-[32px]">
                            <FormHelperText error={formik.errors.email} touched={formik.touched.email} />
                        </div>
                    </FormControl>
                )}

                <FormControl>
                    <Input
                        id="password"
                        name="password"
                        type="password"
                        placeholder={t('placeholders.password')}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password}
                        endAdornment={!formik.errors.password ? <CheckIcon color="secondary" sx={{ fontSize: '0.875rem' }} /> : null}
                    />
                    <div className="min-h-[32px]">
                        <FormHelperText error={formik.errors.password} touched={formik.touched.password} />
                    </div>
                </FormControl>

                <FormControl>
                    <Input
                        id="password_confirmation"
                        name="password_confirmation"
                        type="password"
                        placeholder={t('placeholders.repeat-password')}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password_confirmation}
                        endAdornment={
                            !formik.errors.password_confirmation ? <CheckIcon color="secondary" sx={{ fontSize: '0.875rem' }} /> : null
                        }
                    />
                    <div className="min-h-[32px]">
                        <FormHelperText error={formik.errors.password_confirmation} touched={formik.touched.password_confirmation} />
                    </div>
                </FormControl>

                <FormControl>
                    <Input
                        id="first_name"
                        name="first_name"
                        type="text"
                        placeholder={t('placeholders.name')}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.first_name}
                        endAdornment={!formik.errors.first_name ? <CheckIcon color="secondary" sx={{ fontSize: '0.875rem' }} /> : null}
                    />
                    <div className="min-h-[32px]">
                        <FormHelperText error={formik.errors.first_name} touched={formik.touched.first_name} />
                    </div>
                </FormControl>

                <FormControl>
                    <Input
                        id="last_name"
                        name="last_name"
                        type="text"
                        placeholder={t('placeholders.surname')}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.last_name}
                        endAdornment={!formik.errors.last_name ? <CheckIcon color="secondary" sx={{ fontSize: '0.875rem' }} /> : null}
                    />
                    <div className="min-h-[32px]">
                        <FormHelperText error={formik.errors.last_name} touched={formik.touched.last_name} />
                    </div>
                </FormControl>

                <div className="mt-[65px]">
                    <Button
                        type="submit"
                        size="large"
                        className="w-[320px]"
                        disabled={!formik.isValid || formik.isSubmitting}
                        variant="contained"
                    >
                        {status === 'pending' ? <Loading /> : t('buttons.finish')}
                    </Button>
                </div>
            </form>
        </Box>
    );
};
